import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import QuizIcon from '@mui/icons-material/Quiz';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { useFullscreenContent } from './FullscreenContentContext';
import { FullscreenFillContainer } from './ContentFullscreenFrame';

const formatTimer = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const normalizeQuestions = (quiz = {}) => {
  const raw = Array.isArray(quiz.questions) ? quiz.questions : [];
  return raw.map((question, index) => ({
    id: question.id || `q-${index + 1}`,
    type: question.type || 'multiple_choice',
    questionText: question.questionText || question.question_text || '',
    options: Array.isArray(question.options) ? question.options : [],
    correctAnswer: Array.isArray(question.correctAnswer)
      ? question.correctAnswer
      : Array.isArray(question.correct_answer)
        ? question.correct_answer
        : [],
    marks: Number(question.marks || 1),
    explanation: question.explanation || '',
  }));
};

const scoreQuiz = (questions, answers) => {
  let score = 0;
  questions.forEach((question) => {
    const selected = answers[question.id];
    if (!selected) {
      return;
    }
    const selectedValues = Array.isArray(selected) ? selected : [selected];
    const correctValues = question.correctAnswer.map(String);
    const isCorrect =
      selectedValues.length === correctValues.length &&
      selectedValues.every((value) => correctValues.includes(String(value)));
    if (isCorrect) {
      score += question.marks;
    }
  });
  return score;
};

export default function QuizPlayer({ quiz = {}, onComplete }) {
  const isFullscreen = useFullscreenContent();
  const questions = useMemo(() => normalizeQuestions(quiz), [quiz]);
  const totalMarks = useMemo(
    () => questions.reduce((sum, question) => sum + question.marks, 0),
    [questions],
  );
  const passPercentage = Number(quiz.passMarks ?? quiz.pass_marks ?? quiz.passPercentage ?? 80);

  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [remainingMs, setRemainingMs] = useState(null);
  const [timerExpired, setTimerExpired] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  const timeLimitMinutes = Number(quiz.timeLimit || quiz.time_limit || 0);
  const currentQuestion = questions[currentIndex];

  const handleSubmit = useCallback((auto = false) => {
    const finalScore = scoreQuiz(questions, answers);
    const durationSeconds = startedAt
      ? Math.max(0, Math.round((Date.now() - startedAt) / 1000))
      : 0;
    const answersPayload = questions.map((question) => {
      const selected = answers[question.id];
      const selectedValues = Array.isArray(selected)
        ? selected.map(String)
        : selected === undefined || selected === null || selected === ''
          ? []
          : [String(selected)];
      return {
        questionId: question.id,
        selectedAnswers: selectedValues,
      };
    });
    setScore(finalScore);
    setSubmitted(true);
    if (auto) {
      setTimerExpired(true);
    }
    onComplete?.({
      score: finalScore,
      totalMarks,
      durationSeconds,
      answers: answersPayload,
      contentType: 'quiz',
    });
  }, [answers, onComplete, questions, startedAt, totalMarks]);

  useEffect(() => {
    if (!started || submitted || !timeLimitMinutes) {
      return undefined;
    }

    const expiresAt = Date.now() + timeLimitMinutes * 60 * 1000;
    const tick = () => {
      const next = Math.max(0, expiresAt - Date.now());
      setRemainingMs(next);
      if (next <= 0) {
        handleSubmit(true);
      }
    };

    tick();
    const timerId = setInterval(tick, 1000);
    return () => clearInterval(timerId);
  }, [started, submitted, timeLimitMinutes, handleSubmit]);

  const handleAnswerChange = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < questions.length - 1 ? prev + 1 : prev));
  }, [questions.length]);

  useEffect(() => {
    if (!started || submitted) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      const target = event.target;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable
      ) {
        return;
      }

      const options = currentQuestion?.options || [];

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrev();
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (currentIndex < questions.length - 1) {
          handleNext();
        }
        return;
      }

      if (event.key === 'Enter' && currentIndex === questions.length - 1) {
        event.preventDefault();
        handleSubmit(false);
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        if (!options.length) {
          return;
        }
        event.preventDefault();
        const currentAnswer = answers[currentQuestion.id];
        let optionIndex =
          currentAnswer !== undefined && currentAnswer !== ''
            ? parseInt(currentAnswer, 10)
            : -1;

        if (event.key === 'ArrowDown') {
          optionIndex = optionIndex < options.length - 1 ? optionIndex + 1 : 0;
        } else {
          optionIndex = optionIndex > 0 ? optionIndex - 1 : options.length - 1;
        }

        handleAnswerChange(currentQuestion.id, String(optionIndex));
        return;
      }

      if (/^[1-9]$/.test(event.key)) {
        const optionIndex = parseInt(event.key, 10) - 1;
        if (optionIndex >= 0 && optionIndex < options.length) {
          event.preventDefault();
          handleAnswerChange(currentQuestion.id, String(optionIndex));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    started,
    submitted,
    currentIndex,
    questions.length,
    currentQuestion,
    answers,
    handlePrev,
    handleNext,
    handleSubmit,
    handleAnswerChange,
  ]);

  if (!questions.length) {
    return <Alert severity="info">This quiz has no questions yet.</Alert>;
  }

  if (!started) {
    return (
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <QuizIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {quiz.title || 'Quiz'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${questions.length} questions`} />
              <Chip size="small" label={`${totalMarks} marks`} />
              <Chip size="small" label={`Pass ${passPercentage}%`} color="success" variant="outlined" />
              {timeLimitMinutes > 0 ? (
                <Chip
                  size="small"
                  icon={<TimerOutlinedIcon />}
                  label={`${timeLimitMinutes} min`}
                />
              ) : null}
            </Stack>
          </CardContent>
        </Card>
        <Alert severity="info">
          Questions are shown one at a time. Use Previous/Next or ← → keys to move between questions.
          Use ↑ ↓ or number keys (1–4) to select an answer.
        </Alert>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            setStartedAt(Date.now());
            setStarted(true);
          }}
        >
          Start Quiz
        </Button>
      </Stack>
    );
  }

  if (submitted) {
    const percentage = totalMarks > 0 ? (score / totalMarks) * 100 : 0;
    const passed = percentage >= passPercentage;
    return (
      <Stack spacing={2}>
        <Alert severity={passed ? 'success' : 'warning'}>
          <Typography variant="subtitle1" fontWeight={700}>
            {passed ? 'Quiz passed' : 'Quiz completed'}
          </Typography>
          <Typography variant="body2">
            Score: {score} / {totalMarks} ({percentage.toFixed(1)}%)
          </Typography>
        </Alert>
        <Stack spacing={1}>
          {questions.map((question, index) => {
            const selected = answers[question.id];
            const selectedValues = selected ? [].concat(selected).map(String) : [];
            const correctValues = question.correctAnswer.map(String);
            const isCorrect =
              selectedValues.length === correctValues.length &&
              selectedValues.every((value) => correctValues.includes(value));
            return (
              <Card key={question.id} variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    {index + 1}. {question.questionText}
                  </Typography>
                  <Chip
                    size="small"
                    label={isCorrect ? 'Correct' : 'Incorrect'}
                    color={isCorrect ? 'success' : 'error'}
                    sx={{ mb: 1 }}
                  />
                  {question.explanation ? (
                    <Typography variant="body2" color="text.secondary">
                      {question.explanation}
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    );
  }

  return (
    <FullscreenFillContainer>
      <Stack
        spacing={2}
        sx={{
          height: isFullscreen ? '100%' : 'auto',
          minHeight: isFullscreen ? 0 : undefined,
          flex: isFullscreen ? 1 : undefined,
        }}
      >
      {timeLimitMinutes > 0 && remainingMs !== null ? (
        <Alert
          severity={timerExpired || remainingMs <= 0 ? 'error' : remainingMs < 5 * 60 * 1000 ? 'warning' : 'info'}
          icon={<TimerOutlinedIcon />}
          sx={{ flexShrink: 0 }}
        >
          {timerExpired || remainingMs <= 0
            ? 'Time is up. Your answers are locked.'
            : `Time remaining: ${formatTimer(remainingMs)}`}
        </Alert>
      ) : null}

      <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Question {currentIndex + 1} of {questions.length}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={((currentIndex + 1) / questions.length) * 100}
          sx={{ flex: 1, height: 8, borderRadius: 4 }}
        />
        <Chip
          size="small"
          label={`${Object.keys(answers).length}/${questions.length} answered`}
          variant="outlined"
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        />
      </Stack>

      <Card
        variant="outlined"
        sx={{
          flex: isFullscreen ? 1 : undefined,
          minHeight: isFullscreen ? 0 : undefined,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant={isFullscreen ? 'h5' : 'h6'} fontWeight={700} sx={{ mb: 1 }}>
            {currentQuestion.questionText}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            ← → navigate · ↑ ↓ or 1–{currentQuestion.options.length} select answer
            {currentIndex === questions.length - 1 ? ' · Enter to submit' : ''}
          </Typography>

          <FormControl component="fieldset" fullWidth sx={{ flex: 1 }}>
            <RadioGroup
              value={answers[currentQuestion.id] || ''}
              onChange={(event) => handleAnswerChange(currentQuestion.id, event.target.value)}
            >
              {currentQuestion.options.map((option, optionIndex) => {
                const optionValue = String(optionIndex);
                const optionLabel = option.text || option.label || `Option ${optionIndex + 1}`;
                return (
                  <FormControlLabel
                    key={`${currentQuestion.id}-${optionValue}`}
                    value={optionValue}
                    control={<Radio />}
                    label={optionLabel}
                    sx={{
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      mx: 0,
                      px: 1,
                      alignItems: 'flex-start',
                      py: 0.75,
                    }}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0 }}>
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          disabled={currentIndex === 0}
          onClick={handlePrev}
        >
          Previous
        </Button>

        {currentIndex < questions.length - 1 ? (
          <Button
            variant="contained"
            endIcon={<NavigateNextIcon />}
            onClick={handleNext}
          >
            Next
          </Button>
        ) : (
          <Button variant="contained" color="success" onClick={() => handleSubmit(false)}>
            Submit Quiz
          </Button>
        )}
      </Stack>
      </Stack>
    </FullscreenFillContainer>
  );
}
